import { sanitizeStringForUrl, buildCategoryPath, findCategoryMatch } from './search-utils';
import { useFrame } from '~/shared/utils';
import { useEvents } from '~/shared/hooks/useEvents';
import type { PrimaryCategory } from './search.definitions';
import { useDebounce, useKeyPressEvent } from 'react-use';

import {
    useHits,
    useSearchBox,
    UseSearchBoxProps,
    useConfigure,
    useQueryRules,
} from 'react-instantsearch';
import { useEffect } from 'react';

type Redirect = {
    name?: string;
    url?: string;
};

type RenderingContent = {
    redirect?: Redirect;
};

type SearchResults = {
    renderingContent?: RenderingContent;
};

const queryHook: UseSearchBoxProps['queryHook'] = (query, search) => {
    if (query && query.length >= 3) {
        search(query);
    }
};

export const SearchDataHooks = ({
    query,
    inputRef,
    onSubmitSearch,
}: {
    query: string;
    inputRef: React.RefObject<HTMLInputElement>;
    onSubmitSearch: (query: string, redirectUrl?: string) => void;
}) => {
    const { data } = useFrame();
    const { overlaySearchResultEvent } = useEvents(data);

    useConfigure({ hitsPerPage: 6, clickAnalytics: true });
    const { hits, results } = useHits();
    const { refine, clear } = useSearchBox({ queryHook });
    const { items: customRuleData } = useQueryRules();

    useEffect(() => {
        // We will only log search events if we get a queryID and a valid search term.
        const isValidSearchEvent = !!(results?.queryID && query);

        if (isValidSearchEvent) {
            // For debugging purposes only.
            console.log('OverlaySearchResultEvent', {
                search_term: query,
                number_of_results: results?.nbHits,
                search_url: window.location.href,
            });

            overlaySearchResultEvent({
                search_term: query,
                number_of_results: results?.nbHits,
                search_url: window.location.href,
            });
        }
    }, [results?.queryID]);

    useKeyPressEvent('Escape', () => {
        clear();
        inputRef?.current?.blur();
    });

    useKeyPressEvent('Enter', () => {
        if (!query || inputRef.current !== document.activeElement) return;

        const formattedQuery = sanitizeStringForUrl(query);
        const redirectName = customRuleData?.[0]?.renderingContent?.redirect?.name;
        const redirectUrl = customRuleData?.[0]?.renderingContent?.redirect?.url;

        const primaryCategory = hits[0]?.__primary_category as PrimaryCategory;
        let finalUrl = (results as SearchResults)?.renderingContent?.redirect?.url || '';

        if (redirectName === 'brands') {
            finalUrl = `${redirectUrl}${formattedQuery}`;
        } else if (redirectName === 'categories' && primaryCategory) {
            const categoryMatch = findCategoryMatch(primaryCategory, query);
            if (categoryMatch) {
                const categoryPath = buildCategoryPath(categoryMatch);
                finalUrl = `${redirectUrl}${categoryPath}`;
            }
        }

        inputRef?.current?.blur();
        clear();
        onSubmitSearch(query, finalUrl);

        overlaySearchResultEvent({
            search_term: query,
            number_of_results: results?.nbHits,
            search_url: window.location.href,
        });
    });

    // This will refine (search) for results once every 600ms.
    useDebounce(() => refine(query), 600, [query]);

    return null; // no visual output
};
